import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { Modal, Select, Text } from '@kula-ai/components';
import { useShowToast } from '@/hooks/useShowToast';
import { useTzOptions } from '@/components/formBuilder/formFields/DateField/components/useTzOptions';
import { editMe } from '@/store/me/actions';
import { useUpdateInterviewerTimezones } from './useUpdateInterviewerTimezones';

export interface InterviewerWithoutTimezone {
  id: number;
  name: string;
  avatar?: string;
  email?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  interviewer: InterviewerWithoutTimezone | null;
  onTimezoneUpdated: (
    _updatedInterviewers: { id: number; time_zone: string }[]
  ) => void;
}

export const InterviewerTimezoneModal = ({
  isOpen,
  onClose,
  interviewer,
  onTimezoneUpdated,
}: Props) => {
  const toast = useShowToast();
  const dispatch = useDispatch();
  const { timeZoneOptions, customFilterOption } = useTzOptions();
  const mutation = useUpdateInterviewerTimezones();

  useEffect(() => {
    if (isOpen) dispatch(editMe({}) as never);
  }, [dispatch, isOpen]);

  const [tz, setTz] = useState<string>('');

  const handleClose = useCallback(() => {
    setTz('');
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!interviewer || !tz) return;
    const usersPayload = { [`${interviewer.id}`]: tz };
    try {
      const result = await mutation.mutateAsync(usersPayload);
      if ((result?.errors?.length ?? 0) > 0 || !result) {
        toast({
          title: result?.errors?.[0] || 'Failed to update timezone',
          status: 'error',
        });
        return;
      }
      toast({ title: 'Timezone updated successfully', status: 'success' });
      onTimezoneUpdated([{ id: interviewer.id, time_zone: tz }]);
      handleClose();
    } catch (error: unknown) {
      const e = error as {
        response?: { data?: { errors?: string[] } };
        message?: string;
      };
      toast({
        title:
          e?.response?.data?.errors?.[0] ||
          e?.message ||
          'Failed to update timezone',
        status: 'error',
      });
    }
  }, [interviewer, tz, mutation, onTimezoneUpdated, handleClose, toast]);

  const title = useMemo(
    () =>
      interviewer ? `Set timezone for ${interviewer.name}` : 'Set timezone',
    [interviewer]
  );

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) handleClose();
      }}
      title={title}
      size="md"
      overlayZIndex={1450}
      contentZIndex={1500}
      primaryButton={{
        label: 'Save',
        onClick: handleSave,
        isLoading: mutation.isPending,
        disabled: !tz,
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: handleClose,
        disabled: mutation.isPending,
      }}
    >
      <Box>
        <Text variant="p" color="muted" className="mb-4">
          Set timezone so candidates can see their availability
        </Text>
        <Text variant="p" color="body" className="mb-2">
          Timezone
        </Text>
        <Select
          placeholder="Select timezone"
          value={tz}
          options={timeZoneOptions}
          onChange={(v) => setTz(typeof v === 'string' ? v : '')}
          searchable
          // Inline (not body-portaled) so Radix Dialog's focus-trap
          // doesn't absorb scroll/click on the menu. `menuPosition:
          // fixed` lets the menu overlay the Save/Cancel footer
          // instead of pushing modal layout.
          menuPortalTarget={null}
          reactSelectProps={{
            filterOption: customFilterOption,
            menuPlacement: 'bottom',
            menuPosition: 'fixed',
            maxMenuHeight: 240,
          }}
        />
      </Box>
    </Modal>
  );
};
